import {
  ColumnDef,
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  Row,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { DataTableRenderer } from 'components/table/DataTableRenderer';
import { Tile, TileContent } from 'components/Layout/PageContainer';
import { IntlShape, useIntl } from 'react-intl';
import { useDataTableContext } from 'components/table/context/DataTableContext';
import { DataTableExportButton } from 'components/table/DataTableExportButton';
import { DataTableExportConfigOptions } from 'components/table/DataTableExportUtil';
import { ReportDownload } from 'components/table/ReportDownload';
import { ExpandableRow } from 'components/table/ExpandableRow';

interface DataTableProps<T> {
  titleId: string;
  additionalTitleId?: string;
  additionalTitle?: React.ReactNode;
  rows: T[];
  columns: ColumnDef<T>[];
  maxPageSize?: number;
  defaultSortingState?: SortingState;
  defaultExpandedState?: ExpandedState;
  exportConfigOptions?: DataTableExportConfigOptions;
  subtitleBySortedColumn?: boolean;
  subTitleDecorator?: (subtitle: string) => string;
  onRowClick?: (row: Row<T>) => void;
  isError?: boolean;
  errorMessage?: string | null;
  testId?: string;
  exportName?: string;
}

export interface ColumnMeta {
  sortTitleId?: string;
  sortSubtitleId?: string;
  skipExport: boolean;
  valueExporter?: (cellValue: unknown, intl: IntlShape) => string;
  headerExporter?: (intl: IntlShape) => string;
}

function hasMeta(obj: unknown): obj is { meta: ColumnMeta } {
  return typeof obj === 'object' && obj !== null && 'meta' in obj;
}

type ColumnWithAccessorKey<T> = ColumnDef<T> & { accessorKey?: string };

function hasAccessorKey<T>(col: ColumnDef<T>): col is ColumnWithAccessorKey<T> {
  return (
    typeof col === 'object' &&
    col !== null &&
    'accessorKey' in col &&
    typeof (col as { accessorKey?: unknown }).accessorKey === 'string'
  );
}

export function DataTable<T>({
  titleId,
  additionalTitleId = undefined,
  additionalTitle = undefined,
  rows,
  columns,
  maxPageSize = 10,
  defaultSortingState = [],
  defaultExpandedState = {},
  exportConfigOptions = undefined,
  subtitleBySortedColumn = true,
  subTitleDecorator = (subTitle: string) => subTitle,
  onRowClick = undefined,
  isError = false,
  errorMessage = null,
  testId = undefined,
  exportName = undefined,
}: DataTableProps<T>) {
  const intl = useIntl();

  const { sortedColumnHeader } = useDataTableContext();

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: maxPageSize,
  });

  if (exportName) {
    exportConfigOptions = { ...exportConfigOptions, exportName: exportName };
  }

  const allColumns = columns.flatMap(col => {
    return [col, ...((col as ColumnDef<T> & { columns: ColumnDef<T>[] }).columns ?? [])];
  });

  const [sorting, setSorting] = useState<SortingState>(defaultSortingState);

  const [expanded, setExpanded] = useState<ExpandedState>(defaultExpandedState);

  const table = useReactTable({
    data: rows,
    columns,
    state: {
      pagination,
      sorting,
      expanded,
    },
    getCoreRowModel: getCoreRowModel(),
    getSubRows: row => (row as ExpandableRow<T>).subRows ?? undefined,
    onExpandedChange: setExpanded,
    getExpandedRowModel: getExpandedRowModel(),
    onPaginationChange: setPagination,
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    enableMultiSort: false,
    enableSortingRemoval: false,
    paginateExpandedRows: false,
  });

  const sortSubtitle = useMemo(() => {
    if (additionalTitleId) {
      return intl.formatMessage({ id: additionalTitleId });
    }
    if (subtitleBySortedColumn && sorting.length > 0) {
      const sortedColId = sorting[0].id;
      const sortedColDef = allColumns.find(
        col => col.id === sortedColId || (hasAccessorKey(col) && col.accessorKey === sortedColId)
      );
      if (sortedColDef && hasMeta(sortedColDef) && sortedColDef.meta?.sortTitleId) {
        const meta = sortedColDef.meta;
        const byText = intl.formatMessage({ id: 'trading.drilldown.table.by', defaultMessage: 'by ' });
        const mainTitle = intl.formatMessage({ id: meta.sortTitleId });
        if (meta.sortSubtitleId) {
          const subTitle = intl.formatMessage({ id: meta.sortSubtitleId });
          return `${byText.trim()} ${mainTitle} (${subTitle})`;
        }
        return `${byText.trim()} ${mainTitle}`;
      }
      return sortedColumnHeader;
    }
    return undefined;
  }, [sortedColumnHeader, sorting, columns, intl, additionalTitleId, subtitleBySortedColumn]);

  return (
    <Tile
      titleId={titleId}
      additionalTitle={
        <>
          {sortSubtitle && <span>{subTitleDecorator(sortSubtitle)}</span>}
          {additionalTitle && <span>{additionalTitle}</span>}
        </>
      }
      isError={isError}
      errorDetails={errorMessage}
      contentClassName="min-h-[320px]"
      testId={testId}
      rightHandTitleComponent={
        exportConfigOptions ? (
          exportConfigOptions.reportDownloadProps ? (
            <ReportDownload
              service={exportConfigOptions.reportDownloadProps.service}
              tableName={exportConfigOptions.reportDownloadProps.tableName}
              measuresGroup={exportConfigOptions.reportDownloadProps.measuresGroup}
              requestDecorator={exportConfigOptions.reportDownloadProps.requestDecorator}
              isArticleVisibility={exportConfigOptions.reportDownloadProps.isArticleVisibility}
              isProReport={exportConfigOptions.reportDownloadProps.isProReport}
              disabled={isError}
            />
          ) : (
            <DataTableExportButton table={table} config={exportConfigOptions} disabled={isError} />
          )
        ) : null
      }
    >
      <TileContent className="px-0">
        <DataTableRenderer table={table} pagination={pagination} onRowClick={onRowClick} />
      </TileContent>
    </Tile>
  );
}
